import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  LendingPaymentSource,
  VXLIconResultTypenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useToggleShowAddDebitBottomSheet from 'features/advance//hooks/useToggleShowAddDebitBottomSheet';
import { mapLendingPaymentSourceToPaymentSourceInput } from 'features/advance/helpers/Advance.helpers';
import { getTransferFieldSecondaryText } from 'features/advance/helpers/advanceManualRepay.helpers';
import useRepayAdvanceV2 from 'features/advance/hooks/useRepayAdvanceV2';
import AdvanceAccountListBottomSheet from 'features/advance/screens/registration/AutoPaymentScreen/AdvanceAccountListBottomSheet';
import { formatAccountName } from 'features/advance/screens/registration/AutoPaymentScreen/AdvanceAccountListBottomSheet.helpers';
import { TabaPayEntryFlowType } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import TransferField from 'shared/components/vxl/fields/TransferField/TransferField';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const AdvanceManualRepayment = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'manualRepay',
  });

  const {
    amountDueFormatted,
    transferAmount,
    accountOptionsData,
    isAmountEntered,
    isSubmitDisabled,
    bottomSheetRef,
    limitErrorString,
    isErrorStringRed,
    advanceStatus,
    advanceId,
    handleNumPadAction,
    openBottomSheet,
    loading,
    error,
    openPlaidLink,
    selectedAccount,
    setSelectedAccount,
  } = useRepayAdvanceV2();

  const {
    loading: toggleShowAddDebitBottomSheetLoading,
    hasError: toggleShowAddDebitBottomSheetError,
    execute: executeToggleShowAddDebitBottomSheet,
  } = useToggleShowAddDebitBottomSheet();

  const bottomSheetDebitRef = useRef<BottomSheetModal>(null);
  const showAddDebitCard = accountOptionsData?.showLinkDebitCard ?? false;
  const showAddPlaid = accountOptionsData?.showLinkAccount ?? false;

  const showAddDebitCardBottomSheet =
    accountOptionsData?.showLinkDebitCardSheet ?? false;

  const { bottom } = useSafeAreaInsets();
  const fetchingData = loading || toggleShowAddDebitBottomSheetLoading;
  const errorFetchingData =
    Boolean(error) || toggleShowAddDebitBottomSheetError;

  const amountDue = parseFloat(amountDueFormatted.replace('$', '').trim());
  const currentTransferAmount = parseFloat(transferAmount);
  const isPartialPayment = currentTransferAmount < amountDue;

  useViewDidFocusScreenAnalyticsEvent({
    key: '/accounts/varo-advance/manual-repayments',
  });

  const logRepaymentAccountSelectedEvent = usePressableAnalytics({
    analyticsId: 'vaRepaymentAccountSelected',
  });

  // ToDo: JT-137836 Pass values from Add Debit Card flow to confirm payment screen
  const handleLinkDebitCard = () => {
    router.navigate({
      pathname: '/move-money/tabapay/dcf-tabapay-form',
      params: { tabaPayEntryFlowType: TabaPayEntryFlowType.VaPayment, id },
    });
    bottomSheetRef?.current?.dismiss();
    bottomSheetDebitRef?.current?.dismiss();
  };

  const handlePress = () => {
    logRepaymentAccountSelectedEvent();
    openBottomSheet();
  };

  const handleClose = () => {
    router.push('/');
  };

  const handleNextAction = () => {
    router.push({
      pathname: '/accounts/varo-advance/confirm-payment',
      params: {
        id,
        advanceId,
        repayAmount: transferAmount,
        sourceAccountId: selectedAccount?.accountId,
        accountName: formatAccountName(
          selectedAccount?.name ?? '',
          selectedAccount?.maskedAccountNumber ?? '',
        ),
        pendingAmount: amountDue - currentTransferAmount,
        advanceStatus: advanceStatus,
        paymentSource: selectedAccount?.source,
      },
    });
  };

  useEffect(() => {
    if (showAddDebitCardBottomSheet) {
      void executeToggleShowAddDebitBottomSheet({
        input: { setVisibility: false },
      });
      bottomSheetDebitRef?.current?.present();
    }
  }, [showAddDebitCardBottomSheet, executeToggleShowAddDebitBottomSheet]);

  return (
    <View style={[styles.screen, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView contentContainerStyle={styles.scrollViewContainer}>
        <TransferField
          primaryText={
            selectedAccount
              ? formatAccountName(
                  selectedAccount.name,
                  selectedAccount.maskedAccountNumber || '',
                )
              : translatedText('selectAnAccount')
          }
          secondaryText={getTransferFieldSecondaryText(
            selectedAccount,
            translatedText('available'),
          )}
          labelText={translatedText('from')}
          avatarAccessory={
            selectedAccount
              ? {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-source-varo-logo',
                  props: {
                    iconAccessibilityLabel: 'logo',
                    imageSource: {
                      uri:
                        selectedAccount.icon?.__typename ===
                        VXLIconResultTypenames.URLType
                          ? selectedAccount.icon.url
                          : undefined,
                    },
                    iconKey:
                      selectedAccount.icon?.__typename ===
                      VXLIconResultTypenames.VXLIconType
                        ? selectedAccount.icon.key
                        : undefined,
                    variant: 'MD',
                  },
                }
              : {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-source-bank-icon',
                  props: {
                    iconAccessibilityLabel: 'bank-icon',
                    iconKey: 'BANK',
                    variant: 'MD',
                  },
                }
          }
          onPress={handlePress}
        />

        <View style={styles.percentageMainWrapper}>
          <ExpressiveXXLText
            numberOfLines={0}
            style={styles.updatedSYPPercentage}
          >
            {isAmountEntered ? `$` + transferAmount : amountDueFormatted}
          </ExpressiveXXLText>
        </View>
        {selectedAccount ? (
          <View style={styles.footerContainer}>
            <BodyXSText
              numberOfLines={0}
              style={
                isErrorStringRed
                  ? { color: theme.color.status.negative }
                  : undefined
              }
            >
              {limitErrorString}
            </BodyXSText>
          </View>
        ) : null}
      </ScrollView>

      {!fetchingData ? (
        <View style={styles.numPadContainer}>
          <NumberPad
            submitButtonLabel={translatedText('button')}
            handleButtonPress={handleNumPadAction}
            handleSubmit={handleNextAction}
            submitDisabled={isSubmitDisabled}
            disabled={!selectedAccount}
            analyticsId="vaSelectAmountStepCompleted"
            customAttributes={{
              amount: transferAmount ? transferAmount : '',
              payment_type: isPartialPayment ? 'partial' : 'full',
              payment_method: selectedAccount?.source
                ? mapLendingPaymentSourceToPaymentSourceInput(
                    selectedAccount.source,
                  )
                : undefined,
              institution_name:
                selectedAccount?.source === LendingPaymentSource.InternalDda ||
                selectedAccount?.source === LendingPaymentSource.InternalSavings
                  ? 'Varo'
                  : selectedAccount?.name,
            }}
          />
        </View>
      ) : null}

      <AdvanceAccountListBottomSheet
        ref={bottomSheetRef}
        accountGroups={accountOptionsData?.options ?? []}
        fetching={loading}
        setSelectedAccount={setSelectedAccount}
        showAddPlaid={showAddPlaid}
        setOpenPlaid={openPlaidLink}
        showAddDebitCard={showAddDebitCard}
        setOpenAddDebitCard={handleLinkDebitCard}
        entryPoint="manualPay"
      />
      <BottomSheet ref={bottomSheetDebitRef}>
        <ScrollView contentContainerStyle={styles.bottomSheetScrollView}>
          <View style={styles.illustrationContainer}>
            <View style={styles.illustrationSizeContainer}>
              <Illustration illustration="DEBIT_CARD_A" />
            </View>
          </View>
          <View style={styles.contentContainer}>
            <TitleMdText numberOfLines={1}>
              {translatedText('linkDebitCardTitle')}
            </TitleMdText>
            <BodyBaseText numberOfLines={2}>
              {translatedText('linkDebitCardText')}
            </BodyBaseText>
          </View>
          <View style={[styles.buttonGroup, { paddingBottom: bottom }]}>
            <Button
              label={translatedText('addDebitCard')}
              type="PRIMARY"
              analyticsId="addDebitCard"
              onPress={handleLinkDebitCard}
            />
            <Button
              label={translatedText('notRightNow')}
              type="SECONDARY"
              analyticsId="notRightNow"
              onPress={bottomSheetDebitRef?.current?.dismiss}
            />
          </View>
        </ScrollView>
      </BottomSheet>

      <ScreenTransitionOverlay
        isLoading={fetchingData}
        LoadingComponent={<FullScreenLoading />}
        ErrorComponent={
          errorFetchingData ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              primaryButton={{
                title: translatedText('errorMessage'),
                onPress: handleClose,
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheetScrollView: {
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'center',
    marginBottom: isWeb ? theme.spacing.m : 0,
  },
  buttonGroup: {
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
    width: '100%',
  },
  contentContainer: {
    alignItems: 'center',
    gap: theme.spacing.s,
    marginBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  footerContainer: {
    alignItems: 'center',
    paddingVertical: theme.spacing.s,
  },
  illustrationContainer: {
    marginBottom: theme.spacing.xl,
  },
  illustrationSizeContainer: {
    height: 102,
    width: 102,
  },
  numPadContainer: {
    paddingHorizontal: theme.spacing.m,
    width: '100%',
  },
  percentageMainWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollViewContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  updatedSYPPercentage: {
    textAlign: 'center',
  },
});

export default AdvanceManualRepayment;
